import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar, MobileMenuButton } from './Sidebar';
import { Greeting } from './Greeting';
import { Toaster } from 'sonner';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || '/sobs-logo.png';

export const Layout = () => {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Gate Point users can only access specific pages
  useEffect(() => {
    if (user?.role === 'gate_point') {
      const allowedPaths = ['/admin/gate-point-passes', '/admin/security'];
      const currentPath = location.pathname;
      const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
      if (!isAllowed) {
        navigate('/admin/gate-point-passes', { replace: true });
      }
    }

    // Manifest Officer users can only access Manifests and Security
    if (user?.role === 'manifest_officer') {
      const allowedPaths = ['/admin/manifests', '/admin/security'];
      const currentPath = location.pathname;
      const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
      if (!isAllowed) {
        navigate('/admin/manifests', { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B2545] auth-grid">
        <div className="text-center">
          <img src={LOGO_URL} alt="SoBS" className="h-20 mx-auto mb-6 bg-white p-3 border-t-4 border-[#FFC107]" />
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white/70 text-[11px] font-bold uppercase tracking-[0.18em]">Loading SoBS Gate Pass System</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin" state={{ from: location.pathname }} replace />;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile header only - desktop mirrors imis.sobs.gov.so (sidebar + content) */}
        <header className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="mobile-header">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <MobileMenuButton onClick={() => setSidebarOpen(true)} />
              <img src={LOGO_URL} alt="SoBS" className="h-8 w-auto object-contain shrink-0" />
              <div className="leading-tight hidden min-[400px]:block">
                <span className="font-extrabold text-[#0B2545] text-[13px] block whitespace-nowrap">Staff Portal</span>
                <span className="text-[9px] text-[#B8860B] font-bold uppercase tracking-[0.18em] whitespace-nowrap">SOBS-AAIA</span>
              </div>
            </div>
            <div className="text-right min-w-0 max-w-[45%]" data-testid="header-user">
              <p className="text-xs font-bold text-[#0B2545] leading-none truncate">{user?.full_name}</p>
              <p className="text-[9px] uppercase tracking-[0.12em] font-bold text-[#FF5722] mt-1 truncate">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="h-[3px] bg-[#FFC107]" />
        </header>

        {/* Main Content with washed-out brand watermark */}
        <main className="relative p-4 lg:p-8 flex-1" data-testid="main-content">
          <div className="brand-watermark" aria-hidden="true" data-testid="brand-watermark" />
          <div className="relative z-[1]">
            <Greeting />
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-[#0B2545] text-white/70 py-4 px-6 border-t-[3px] border-[#FFC107]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">Somali Bureau of Standards</span>
              <span className="hidden sm:inline text-white/40">|</span>
              <span className="hidden sm:inline">Aden Abdulle International Airport · Gate Pass Portal</span>
            </div>
            <span className="uppercase tracking-[0.14em] text-[10px] font-semibold">© {new Date().getFullYear()} SoBS · Hotline 2022 · info@sobs.gov.so</span>
          </div>
        </footer>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
